import { useEffect, useId } from 'react';
import { AlertTriangle, Info, X } from 'lucide-react';

export interface ConfirmActionModalProps {
  title: string;
  description: React.ReactNode;
  warning?: React.ReactNode;
  children?: React.ReactNode;
  confirmButtonLabel?: string;
  variant?: 'danger' | 'warning' | 'default';
  isSubmitting?: boolean;
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}

const VARIANT_STYLES = {
  danger: {
    headerBg: 'bg-red-50',
    headerIcon: 'text-red-600',
    headerTitle: 'text-red-900',
    closeBtn: 'text-red-400 hover:text-red-600',
    confirmBtn: 'bg-red-600 text-white hover:bg-red-700',
  },
  warning: {
    headerBg: 'bg-amber-50',
    headerIcon: 'text-amber-600',
    headerTitle: 'text-amber-900',
    closeBtn: 'text-amber-400 hover:text-amber-600',
    confirmBtn: 'bg-amber-600 text-white hover:bg-amber-700',
  },
  default: {
    headerBg: 'bg-gray-50',
    headerIcon: 'text-gray-500',
    headerTitle: 'text-gray-900',
    closeBtn: 'text-gray-400 hover:text-gray-600',
    confirmBtn: 'bg-gray-700 text-white hover:bg-gray-800',
  },
} as const;

export default function ConfirmActionModal({
  title,
  description,
  warning,
  children,
  confirmButtonLabel = 'Yes, Continue',
  variant = 'danger',
  isSubmitting = false,
  onConfirm,
  onClose,
}: ConfirmActionModalProps) {
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const styles = VARIANT_STYLES[variant];
  const HeaderIcon = variant === 'default' ? Info : AlertTriangle;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    await onConfirm();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={descriptionId}>
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col overflow-hidden max-h-[calc(100vh-2rem)]">
        <div className={`px-4 py-3 border-b flex items-center gap-2 ${styles.headerBg}`}>
          <HeaderIcon className={`h-5 w-5 flex-shrink-0 ${styles.headerIcon}`} />
          <h3 id={titleId} className={`font-semibold ${styles.headerTitle}`}>{title}</h3>
          <button type="button" onClick={onClose} aria-label={`Close ${title}`} className={`ml-auto transition-colors ${styles.closeBtn}`}>
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4 overflow-y-auto">
          <p id={descriptionId} className="text-sm text-gray-600">{description}</p>
          {children}
          {warning && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
              <div className="text-xs text-amber-800 font-medium flex items-start gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <div>{warning}</div>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting}
              className={`px-3 py-2 text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${styles.confirmBtn}`}>
              {isSubmitting ? 'Processing...' : confirmButtonLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
